import { useEffect, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import DeleteIcon from '@material-ui/icons/Delete';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

type AccessType = 'RW' | 'RW_SHARED' | 'RO_SHARED';

export type BlockVolumeSpec = {
  mode: 'existing' | 'new';
  volumeId?: string;
  name?: string;
  sizeGb?: number;
  vpusPerGb?: number;
  backupPolicyId?: string;
  kmsKeyId?: string;
  freeformTags?: Record<string, string>;
  accessType: AccessType;
};

const ACCESS_TYPE_LABELS: Record<AccessType, string> = {
  RW: 'Read/Write',
  RW_SHARED: 'Read/Write - Shareable',
  RO_SHARED: 'Read Only - Shareable',
};

function parseTags(input: string): Record<string, string> | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  const tags: Record<string, string> = {};
  trimmed.split(',').forEach(pair => {
    const [key, value] = pair.split('=').map(s => s.trim());
    if (key && value) tags[key] = value;
  });
  return Object.keys(tags).length > 0 ? tags : undefined;
}

function AttachExistingForm({
  region,
  compartmentId,
  onAdd,
}: {
  region: string;
  compartmentId: string;
  onAdd: (spec: BlockVolumeSpec) => void;
}) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [volumes, setVolumes] = useState<
    { id: string; displayName: string; sizeInGBs: number }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [accessType, setAccessType] = useState<AccessType>('RW');

  useEffect(() => {
    setLoading(true);
    setError('');
    discoveryApi.getBaseUrl('infrastructure').then(baseUrl => {
      fetchApi
        .fetch(
          `${baseUrl}/oci/block-volumes?region=${encodeURIComponent(
            region,
          )}&compartmentId=${encodeURIComponent(compartmentId)}`,
        )
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setError(
              typeof data.error === 'string'
                ? data.error
                : data.error?.message ?? 'Failed to load block volumes',
            );
          } else {
            setVolumes(data.volumes ?? []);
          }
        })
        .catch(() => setError('Failed to load block volumes'))
        .finally(() => setLoading(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, compartmentId]);

  if (loading) return <CircularProgress size={20} />;
  if (error) return <FormHelperText error>{error}</FormHelperText>;

  if (volumes.length === 0) {
    return (
      <FormHelperText>
        No Block Volumes available in this compartment.
      </FormHelperText>
    );
  }

  const selected = volumes.find(v => v.id === selectedId);

  return (
    <Box>
      <FormControl fullWidth style={{ marginBottom: 8 }}>
        <InputLabel>Volume</InputLabel>
        <Select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value as string)}
        >
          {volumes.map(v => (
            <MenuItem key={v.id} value={v.id}>
              {v.displayName} ({v.sizeInGBs} GB)
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <AccessTypeRadio value={accessType} onChange={setAccessType} />
      <Button
        variant="outlined"
        disabled={!selectedId}
        onClick={() => {
          onAdd({
            mode: 'existing',
            volumeId: selectedId,
            name: selected?.displayName,
            sizeGb: selected?.sizeInGBs,
            accessType,
          });
          setSelectedId('');
        }}
      >
        Attach
      </Button>
    </Box>
  );
}

const MAX_NEW_VOLUME_SIZE_GB = 1024;

function CreateNewForm({ onAdd }: { onAdd: (spec: BlockVolumeSpec) => void }) {
  const [name, setName] = useState('');
  const [sizeGb, setSizeGb] = useState(50);
  const [vpusPerGb, setVpusPerGb] = useState(10);
  const [backupPolicyId, setBackupPolicyId] = useState('');
  const [kmsKeyId, setKmsKeyId] = useState('');
  const [tags, setTags] = useState('');
  const [accessType, setAccessType] = useState<AccessType>('RW');
  const sizeTooLarge = sizeGb > MAX_NEW_VOLUME_SIZE_GB;

  return (
    <Box>
      <TextField
        label="Volume Name"
        fullWidth
        value={name}
        onChange={e => setName(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <TextField
        label="Size (GB)"
        type="number"
        value={sizeGb}
        error={sizeTooLarge}
        helperText={
          sizeTooLarge
            ? `Max ${MAX_NEW_VOLUME_SIZE_GB} GB`
            : `Up to ${MAX_NEW_VOLUME_SIZE_GB} GB`
        }
        onChange={e => setSizeGb(Number(e.target.value))}
        style={{ marginRight: 16 }}
      />
      <TextField
        label="Performance (VPUs/GB)"
        type="number"
        value={vpusPerGb}
        onChange={e => setVpusPerGb(Number(e.target.value))}
      />
      <TextField
        label="Backup Policy ID (optional)"
        fullWidth
        value={backupPolicyId}
        onChange={e => setBackupPolicyId(e.target.value)}
        style={{ marginTop: 8, marginBottom: 8 }}
      />
      <TextField
        label="Encryption Key OCID (optional, Oracle-managed if blank)"
        fullWidth
        value={kmsKeyId}
        onChange={e => setKmsKeyId(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <TextField
        label="Tags (key=value, key2=value2)"
        fullWidth
        value={tags}
        onChange={e => setTags(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <AccessTypeRadio value={accessType} onChange={setAccessType} />
      <Button
        variant="outlined"
        disabled={!name || !sizeGb || sizeTooLarge}
        onClick={() => {
          onAdd({
            mode: 'new',
            name,
            sizeGb,
            vpusPerGb,
            backupPolicyId: backupPolicyId || undefined,
            kmsKeyId: kmsKeyId || undefined,
            freeformTags: parseTags(tags),
            accessType,
          });
          setName('');
        }}
      >
        Add
      </Button>
    </Box>
  );
}

function AccessTypeRadio({
  value,
  onChange,
}: {
  value: AccessType;
  onChange: (value: AccessType) => void;
}) {
  return (
    <FormControl component="fieldset" style={{ margin: '8px 0' }}>
      <RadioGroup
        row
        value={value}
        onChange={e => onChange(e.target.value as AccessType)}
      >
        {(Object.keys(ACCESS_TYPE_LABELS) as AccessType[]).map(key => (
          <FormControlLabel
            key={key}
            value={key}
            control={<Radio size="small" />}
            label={ACCESS_TYPE_LABELS[key]}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

export function OciBlockVolumeManagerComponent({
  onChange,
  formData,
  formContext,
}: any) {
  const fd = (formContext as any)?.formData ?? {};
  const region: string = fd.region ?? '';
  const compartmentId: string = fd.compartmentId ?? '';
  const volumes: BlockVolumeSpec[] = formData ?? [];
  const [mode, setMode] = useState<'existing' | 'new'>('existing');

  useEffect(() => {
    if (!formData) {
      onChange([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!region || !compartmentId) {
    return (
      <FormHelperText>Select a region and compartment first</FormHelperText>
    );
  }

  // Only one attached block volume is supported.
  const addVolume = (spec: BlockVolumeSpec) => onChange([spec]);
  const removeVolume = (index: number) =>
    onChange(volumes.filter((_, i) => i !== index));

  return (
    <Box>
      {volumes.length === 0 && (
        <>
          <ButtonGroup size="small" style={{ marginBottom: 16 }}>
            <Button
              variant={mode === 'existing' ? 'contained' : 'outlined'}
              color={mode === 'existing' ? 'primary' : 'default'}
              onClick={() => setMode('existing')}
            >
              Attach Existing Volume
            </Button>
            <Button
              variant={mode === 'new' ? 'contained' : 'outlined'}
              color={mode === 'new' ? 'primary' : 'default'}
              onClick={() => setMode('new')}
            >
              Create New Block Volume
            </Button>
          </ButtonGroup>

          {mode === 'existing' ? (
            <AttachExistingForm
              region={region}
              compartmentId={compartmentId}
              onAdd={addVolume}
            />
          ) : (
            <CreateNewForm onAdd={addVolume} />
          )}
        </>
      )}

      {volumes.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>
            Attached Volume
          </Typography>
          <FormHelperText style={{ marginBottom: 8 }}>
            Only one block volume can be attached. Remove it to attach a
            different one.
          </FormHelperText>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Size (GB)</TableCell>
                <TableCell>Access</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {volumes.map((v, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={index}>
                  <TableCell>{v.name || v.volumeId}</TableCell>
                  <TableCell>{v.mode === 'new' ? 'New' : 'Existing'}</TableCell>
                  <TableCell>{v.sizeGb ?? '—'}</TableCell>
                  <TableCell>{ACCESS_TYPE_LABELS[v.accessType]}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => removeVolume(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export const OciBlockVolumeManagerField = createFormField({
  name: 'OciBlockVolumeManager',
  component: OciBlockVolumeManagerComponent,
});
